import { Button, Checkbox, Input, Modal, Space, Switch, Table, Typography } from "antd"
import { useContext, useEffect, useReducer } from "react"
import VSpace from "../../common/VSpace"
import { ItemsContext } from "../../Items/ItemsRoot"
import { useSaveTemplateMutation } from "../../Templates/templateQueries"
import { createChangeSet, createModel, defaultModel, reducer, SaveAsTemplateItem } from "./saveAsTemplateModel"
import './SaveAsTemplate.css'
import LabeledSwitch from "../../common/LabeledSwitch"

const { Text } = Typography

export type Props = {
  open: boolean
  onClose: () => void
  workareaId: number
}

const SaveToTemplateModal = ({ open, onClose, workareaId }: Props) => {
  const { jobData, jobItemsQuery } = useContext(ItemsContext)
  const workarea = jobData.workareas.find(x => x.id === workareaId)!
  const estimatedItems = jobItemsQuery.data?.estimatedItems.filter(x => x.workareaId === workareaId) ?? []
  const catalogItems = jobItemsQuery.data?.catalogItems ?? []
  const [model, dispatch] = useReducer(reducer, defaultModel())

  const saveTemplateMutation = useSaveTemplateMutation((id) => {
    onClose()
    window.open(`/#!/items/templates#${id}`)
  })

  const handleSave = () => {
    saveTemplateMutation.mutate({
      id: 0,
      changeSet: createChangeSet(model, workarea, catalogItems),
    })
  }

  useEffect(() => {
    if (workarea && jobItemsQuery.data) {
      dispatch({ type: 'RESET_FORM', model: createModel(workarea, estimatedItems, catalogItems) })
    }
  }, [jobItemsQuery.data, workareaId])

  const toggleItem = (item: SaveAsTemplateItem, enabled?: boolean) => {
    dispatch({
      type: 'ITEM_TOGGLED',
      estimatedItemId: item.estimatedItemId,
      enabled: enabled ?? !model.templateItems.find(x => x.estimatedItemId === item.estimatedItemId)?.enabled
    })
  }

  const someUseQuantity = model.templateItems.some(x => x.useQuantity)
  const allUseQuantity = model.templateItems.every(x => x.useQuantity)

  return <Modal
    title="Create Template"
    open={open}
    onCancel={onClose}
    className="save-as-template"
    footer={[
      <Button key="cancel" onClick={onClose}>Cancel</Button>,
      <Button key="save" type="primary" onClick={handleSave} loading={saveTemplateMutation.isLoading}>Save</Button>
    ]}
  >
    <VSpace size="middle">
      <label>Name
        <Input value={model.name} onChange={e => dispatch({ type: 'NAME_CHANGED', name: e.target.value })} maxLength={150} />
      </label>
      <Checkbox checked={allUseQuantity} indeterminate={!allUseQuantity && someUseQuantity} onChange={e => dispatch({ type: 'USE_ITEM_QUANTITIES_CHANGED', enabled: e.target.checked })}>
        Save quantities as default on template
      </Checkbox>
      <Table
        dataSource={model.templateItems}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
          },
          {
            title: 'Save Quantity',
            dataIndex: 'useQuantity',
            render: (useQuantity, item) => {
              const text = `${item.quantity} ${item.units ?? ''}`
              return <Space size="large">
                <Switch
                  checked={useQuantity}
                  onChange={enabled => dispatch({ type: 'USE_ITEM_QUANTITY_CHANGED', estimatedItemId: item.estimatedItemId, enabled })}
                />
                {item.useQuantity ? text : <Text delete>{text}</Text>}
              </Space>
            }
          },
        ]}
        rowKey="estimatedItemId"
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: model.templateItems.filter(x => x.enabled).map(item => item.estimatedItemId),
          onChange: (selectedRowKeys) => {
            model.templateItems.forEach(item => toggleItem(item, selectedRowKeys.includes(item.estimatedItemId)))
          }
        }}
        scroll={{ y: 400 }}
        pagination={false}
      />
      <LabeledSwitch
        value={!!model.copyCustomerAndInvoiceNotes}
        onChange={enabled => dispatch({ type: 'COPY_CUSTOMER_AND_INVOICE_NOTES_CHANGED', enabled })}
        label="Copy Proposal Description"
      />
      <LabeledSwitch
        value={!!model.copyInvoiceDescription}
        onChange={enabled => dispatch({ type: 'COPY_INVOICE_DESCRIPTION_CHANGED', enabled })}
        label="Copy Invoice Description"
      />
      <LabeledSwitch
        label="Copy Internal Notes"
        value={!!model.copyInternalNotes}
        onChange={enabled => dispatch({ type: 'COPY_INTERNAL_NOTES_CHANGED', enabled })}
      />

    </VSpace>

  </Modal>
}

export default SaveToTemplateModal
